import React, { useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { appConfig } from '../../config/app.config';
import { ButtonTypes, COMPONENT_TYPES } from '../../config/constants';
import h, { isTVodProgram } from '../../helpers/common';
import user from '../../helpers/user';
import { __ } from '../../translations';
import AddWatchlistButton from './AddWatchlistButton';
import LockIcon from './LockIcon';
import { MovieCard } from '@intigral/ui';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import playerService from '../../helpers/bitmovinPlayer';
import { trackCarouselItemDetailClick } from '../../helpers/trackCarouselItemDetailClick';
import useDetailOverlay from '../../hooks/useDetailOverlay';
import usePPVManagement from '../../hooks/usePPVManagement';
import { useConfiguration } from '../../hooks/useConfiguration';
import { useTvod } from '../../hooks/useTvod';
import { RentTimer } from '@intigral/shared';
import useTimeToExpire from '../../hooks/useTimeToExpire';
import { useGetBookMarksQuery } from '@intigral/domain/lib/presentation/hooks/useGetBookMarksQuery';
import { getCountry } from '../../helpers/common';
import { isSubscriptionLockedContent, showUpgradeOrOpenDetail } from '../../helpers/vod';
import { MoviePlayButton } from './MoviePlayButton';

interface WatchListVodItemProps {
    item: any;
    onRemove: () => void;
    componentType: string;
    componentName: string;
    id: string;
    handleSearchAction?: () => void;
    meta?: any;
}

const WatchListVodItem: React.FC<WatchListVodItemProps> = ({
    item,
    onRemove,
    componentType,
    componentName,
    id,
    handleSearchAction,
    meta,
}) => {
    // Use PPV management hook for all PPV-related functionality
    const { shouldShowLock } = usePPVManagement();

    const { operator } = user.getUserInfo();
    const isTVODPurchaseEnabled = user.getTVODPurchaseOption(operator) && isTVodProgram(item);
    const channelId = item?.channelId || item?.channelID || item?.id;
    const { isLocked, enhancedChannel } = shouldShowLock(item, channelId);
    const { data: newConfig } = useConfiguration();
    const [isLoaded, setIsLoaded] = useState(false);

    const { data: bookmarks } = useGetBookMarksQuery({
        apiKey: user.getAPIKey(),
        profileGuid: user.getProfile()?.profileGuid,
        authToken: user.getUserToken(),
        identityToken: user.getIdentityToken(),
        country: getCountry(),
        device: h.getDevice(),
        programType: 'all',
    });

    const isBookmarked = bookmarks?.find((bookmark) => bookmark.paId === item?.Id);

    const onLoadHandler = () => {
        setIsLoaded(true);
    };

    const onClick = () => {
        if (componentType === COMPONENT_TYPES.SEARCH_RESULTS) handleSearchAction();
    };

    const openDetailOverlay = useDetailOverlay();
    const dispatch = useDispatch();
    const { track = () => undefined } = useContext(MixpanelContext) || {};

    const { priceInfo, isRented, isTvod, expiryDate } = useTvod(item);
    const { priceWithVat = '', currency = '' } = priceInfo || {};
    const { durationInMinutes, hours, minutes } = useTimeToExpire(expiryDate);

    let tvodRentNowBtnText = __('tvod_rent_now_button_details') || '';
    if (priceWithVat) {
        tvodRentNowBtnText = tvodRentNowBtnText.replace('{priceWithVat}', `<label  dir="ltr"> ${priceWithVat}`);
    } else {
        tvodRentNowBtnText = tvodRentNowBtnText.replace(' - {priceWithVat}', ``);
    }
    tvodRentNowBtnText = tvodRentNowBtnText.replace('{currency}', `${currency}</label>`);
    // Replace <label> with <span>do
    tvodRentNowBtnText = tvodRentNowBtnText.replace(/<label(.*?)>/g, '<span$1>').replace(/<\/label>/g, '</span>');

    const formatTvodPrice = (priceWithVat, currency, isRented) => {
        if (isRented) {
            return __('tvod_poster_rented');
        }

        return `${priceWithVat} ${currency}`;
    };

    const openVodDetailOverlay = () => {
        if (item?.useCaseId) {
            playerService.setUseCaseId(item.useCaseId);
        }
        openDetailOverlay(item);
    };

    const handleCardClick = () => {
        trackCarouselItemDetailClick({ track, item, componentType, componentName, meta });
        showUpgradeOrOpenDetail({ item, locked, dispatch, openDetail: openVodDetailOverlay });
    };

    const locked = (!isTvod && isLocked) || item?.isLocked;
    const isSubscriptionLocked = isSubscriptionLockedContent(item, locked);

    return (
        <div className="movie-card masonry-item" id={id}>
            <MovieCard
                key={item.id}
                cover={
                    item.image
                        ? h.getImagizerUrl(item.image, newConfig.imagizer?.components_images.WATCHLIST_VOD_ITEM)
                        : appConfig.defaultPortraitImage
                }
                play={
                    <MoviePlayButton
                        isTvod={isTvod}
                        isRented={isRented}
                        item={item}
                        componentName={componentName}
                        componentType={componentType}
                        meta={meta}
                        tvodRentText={tvodRentNowBtnText}
                        openDetailOverlay={openDetailOverlay}
                        hours={hours}
                        minutes={minutes}
                        locked={locked}
                        isComingSoonItem={false}
                    />
                }
                info={[
                    ...(item?.provider?.[0] ? [item.provider[0]] : []),
                    ...(item && Array.isArray(item.genre) ? item.genre.map((c) => __(c)) : []),
                ]}
                timer={
                    isTvod &&
                    isRented && (
                        <RentTimer
                            timeCount={hours > 0 ? [hours, 'hour'] : [minutes, 'minute']}
                            timeType={
                                hours > 0
                                    ? __('tvod_rent_expiration_hours')
                                    : minutes > 0
                                    ? __('tvod_rent_expiration_minutes')
                                    : ''
                            }
                            labelRemaining={__('tvod_rent_remaining_time')}
                            {...(hours < 0 && minutes < 0 ? { expired: true } : {})}
                            expiredLabel={__('tvod_rent_expired')}
                            {...(Number(durationInMinutes) < Number(h.getTVodConfig()?.diffExpirationTimeInMinutes)
                                ? { almostEnded: true }
                                : {})}
                        />
                    )
                }
                price={isTvod && priceWithVat && formatTvodPrice(priceWithVat, currency, isRented)}
                heading={item.text}
                picture={h.getImagizerUrl(item.image_landscape)}
                watchlistButton={
                    <AddWatchlistButton
                        item={item}
                        type={ButtonTypes.VodeItem}
                        onRemove={onRemove}
                        componentType={componentType}
                        componentName={componentName}
                    />
                }
                page={
                    isSubscriptionLocked
                        ? undefined
                        : {
                              onClick: (e) => {
                                  e.stopPropagation();
                                  openVodDetailOverlay();
                              },
                          }
                }
                onClick={handleCardClick}
            />
            {isLocked && !isTVodProgram(item) && <LockIcon filled />}
        </div>
    );
};
export default WatchListVodItem;
